import {
	AfterViewInit,
	Component,
	ElementRef,
	Inject,
	OnInit,
	Renderer2,
	ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { ModalData } from '@datachain/ui-sdk/components/app-modal';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import {
	CellHoverChangedEvent,
	CellPreparedEvent,
	RowClickEvent,
	RowPreparedEvent,
	SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import {
	BehaviorSubject,
	debounceTime,
	firstValueFrom,
	map,
	Observable,
	Subject,
	takeUntil,
	tap,
} from 'rxjs';

import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import {
	refreshSwitchableConsumedProjects,
	refreshSwitchableConsumedProjectsForSubscription,
	SubscriptionSelector,
	switchVersionForConsumedProjects,
	switchVersionForConsumedProjectsOnView,
} from '../../store';
import {
	SwitchableVersionProjectItemEntity,
	VersionSwitchBlockedReason,
} from './switchable-version-project-item.entity';
import { SwitchableVersionProjectListEntity } from './switchable-version-project-list.entity';
import { SwitchableVersionProjectListProvider } from './switchable-version-project-list.provider';
import { VersionSwitcherViewProvider } from './version-switcher-view.provider';

@Component({
	selector: 'app-version-switcher-modal',
	templateUrl: './version-switcher-modal.component.html',
	styleUrls: ['./version-switcher-modal.component.scss'],
	providers: [
		SwitchableVersionProjectListProvider,
		VersionSwitcherViewProvider,
	],
})
export class VersionSwitcherModalComponent<T extends { inViewMode: boolean }>
	extends DcBaseComponent
	implements OnInit, AfterViewInit
{
	protected readonly DcIcons = DcIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly ActionIcons = ActionIcons;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly VersionSwitchBlockedReason = VersionSwitchBlockedReason;

	@ViewChild(DxDataGridComponent)
	public grid: DxDataGridComponent | null = null;

	public currentlyHovered = -1;
	public selectionCount = 0;
	public toggleActions = false;

	public vo$: Observable<{
		switchableProjectList: SwitchableVersionProjectListEntity;
		openedOverlay: number | undefined;
		isSwitchingVersion: boolean;
		inViewMode: boolean;
	}>;

	private readonly isGridReadySubject$ = new Subject<boolean>();

	private readonly openedOverlay$ = new BehaviorSubject<number | undefined>(
		undefined
	);

	public constructor(
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T,
		private readonly switchableProjectListProvider: SwitchableVersionProjectListProvider,
		private readonly selector: SubscriptionSelector,
		private readonly store: Store,
		private readonly versionSwitcherViewProvider: VersionSwitcherViewProvider,
		private readonly renderer: Renderer2,
		private readonly elemRef: ElementRef
	) {
		super();
		this.cmpId = 'version-switcher-modal';

		if (this.modalData.inViewMode) {
			this.switchableProjectListProvider.createAvailableColumnsWithoutSubscription();
		} else {
			this.switchableProjectListProvider.createAvailableColumnsWithSubscription();
		}

		const switchableConsumedProjectList$ = this.selector
			.getSwitchableConsumedProjects$()
			.pipe(
				map((items) =>
					this.switchableProjectListProvider.setListElements(items)
				)
			);

		this.vo$ = combineAll({
			switchableProjectList: switchableConsumedProjectList$,
			openedOverlay: this.openedOverlay$,
			isSwitchingVersion: this.selector.isSwitchingVersion$(),
			inViewMode: this.modalData.inViewMode,
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.handleSubscription();
	}

	public override ngAfterViewInit(): void {
		super.ngAfterViewInit();
		setTimeout(() => {
			this.versionSwitcherViewProvider.adjustHeights(
				this.elemRef,
				this.renderer
			);
		}, 500);
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}

	public refresh(): void {
		if (this.modalData.inViewMode) {
			return this.store.dispatch(
				refreshSwitchableConsumedProjectsForSubscription()
			);
		}
		this.store.dispatch(refreshSwitchableConsumedProjects());
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public onRowPrepared(e: RowPreparedEvent): void {
		if (e.rowType === 'data' && !e.data.switchable) {
			e.rowElement.classList.add('disabled-row');
		}
	}

	public async onRowClick(e: RowClickEvent): Promise<void> {
		const rowData = e.data as SwitchableVersionProjectItemEntity;

		if (rowData?.switchable === false) {
			const previous = await firstValueFrom(this.openedOverlay$);

			if (previous === rowData.id) {
				this.openedOverlay$.next(undefined);
				return;
			}

			this.openedOverlay$.next(rowData.id);
			return;
		}
		const grid = e.component;
		if (grid.isRowSelected(e.key)) {
			await grid.deselectRows([e.key]);
		} else {
			await grid.selectRows([e.key], true);
		}
	}

	public onCellPrepared(e: CellPreparedEvent): void {
		if (
			e.rowType === 'data' &&
			e.column?.type === 'selection' &&
			e.data.switchable === false
		) {
			e.cellElement
				.querySelector('.dx-select-checkbox')
				?.classList.add('dx-state-disabled');
		}
	}

	public onSelectionChange(e: SelectionChangedEvent): void {
		const selectedProjects =
			e.selectedRowsData as Array<SwitchableVersionProjectItemEntity>;
		const keysToDeselect = selectedProjects
			.filter((row) => !row.switchable)
			.map((row) => row.id);

		if (keysToDeselect.length) {
			this.grid?.instance.deselectRows(keysToDeselect);
		}

		this.selectionCount = selectedProjects.filter(
			(row) => row.switchable
		).length;
	}

	public onGridReady(): void {
		this.isGridReadySubject$.next(true);
	}

	public async openPopover(event: Event, key: number): Promise<void> {
		event.stopPropagation();

		const previous = await firstValueFrom(this.openedOverlay$);

		if (previous === key) {
			this.openedOverlay$.next(undefined);
			return;
		}

		this.openedOverlay$.next(key);
	}

	public closeColumnsPopover(): void {
		this.openedOverlay$.next(undefined);
	}

	public launchAction(): void {
		if (!this.grid) {
			return;
		}

		const selected = this.grid.instance.getSelectedRowKeys() as Array<number>;

		if (this.modalData.inViewMode) {
			return this.store.dispatch(
				switchVersionForConsumedProjectsOnView({
					projectIds: selected,
				})
			);
		}

		this.store.dispatch(
			switchVersionForConsumedProjects({
				projectIds: selected,
			})
		);
	}

	private handleSubscription(): void {
		this.isGridReadySubject$
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(300),
				tap(() => {
					this.grid?.instance.updateDimensions();
				})
			)
			.subscribe();
	}
}
